import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Handshake, MessageCircle, Package } from "lucide-react";
import { Button, Skeleton, SkeletonText } from "@/components/ui";
import {
  EscrowLedger,
  escrowIndexForDeliverables,
} from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  useCollaboration,
  useCollaborationMutations,
  type ReviewDecision,
} from "@/hooks/useCollaborations";
import CommissionSplit from "./components/CommissionSplit";
import CollaborationChat from "./components/CollaborationChat";
import DeliverableCard from "./components/DeliverableCard";
import OfferChain from "./components/OfferChain";
import TurnBadge from "./components/TurnBadge";

type MobileTab = "agreement" | "chat" | "deliverables";

/**
 * The collaboration room, with the escrow rail as its spine.
 *
 * The rail runs across the top because it answers the question both sides
 * actually open this screen with — where is the money — before either of them
 * reads a message or a rate. Everything below it is how that state changes.
 *
 * The two columns are flex with wrap, never a grid. A fixed two-column grid
 * cannot reflow: at 1100px it crushes the aside to nothing and the chat becomes
 * a column of single words. Content gets a 480px floor, the aside 320px, and
 * they stack when there is no room for both.
 */
export default function CollaborationRoom() {
  const { collaborationId } = useParams<{ collaborationId: string }>();
  const { t } = useTranslation();
  const { collaboration, loading } = useCollaboration(collaborationId);
  const mutations = useCollaborationMutations();
  const [tab, setTab] = useState<MobileTab>("deliverables");

  if (loading && !collaboration) {
    return (
      <div className="adv-page">
        <Skeleton className="h-4 w-40 rounded-sm" />
        <div className="mt-5 rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
          <div className="flex items-center gap-3">
            <Skeleton className="size-11 rounded-avatar-square" />
            <div className="flex-1">
              <Skeleton className="h-4 w-48 rounded-sm" />
              <Skeleton className="mt-2 h-3 w-64 rounded-sm" />
            </div>
          </div>
          <Skeleton className="mt-6 h-[30px] w-full rounded-sm" />
        </div>
        <div className="mt-4 flex flex-wrap gap-5">
          <div className="flex-[1_1_480px] rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
            <Skeleton className="aspect-16/10 w-full rounded-panel" />
            <SkeletonText className="mt-4" lines={3} />
          </div>
          <div className="flex-[1_1_320px] rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
            <SkeletonText lines={6} widths={[88, 54, 72, 46, 80, 62]} />
          </div>
        </div>
      </div>
    );
  }

  if (!collaboration) {
    return (
      <div className="adv-page">
        <div className="mx-auto max-w-lg rounded-card border border-neutral-200 bg-white px-5 py-16 text-center">
          <p className="text-card-title-sm text-neutral-900">
            {t("marketplace.collaboration.notFoundTitle")}
          </p>
          <p className="mt-2 text-meta text-neutral-500">
            {t("marketplace.collaboration.notFoundBody")}
          </p>
          <Button variant="secondary" className="mt-5" asChild>
            <Link to="/colaboraciones">{t("marketplace.collaboration.backToInbox")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isBrand = collaboration.viewerParty === "BRAND";
  const counterparty = isBrand ? collaboration.creator : collaboration.brand;

  // What this collaboration is worth from the viewer's side of the table: the
  // brand's total including commission, or the creator's payout, which is the
  // full agreed rate because commission is charged on top of it.
  const railAmount = formatMoney(
    isBrand ? collaboration.money.brandTotal : collaboration.money.creatorPayout,
    collaboration.money.currency,
  );

  const deliverableActions = {
    submitDraft: async (id: string, assetUrls: string[]) => {
      await mutations.submitDraft(id, assetUrls);
    },
    review: async (id: string, decision: ReviewDecision, note?: string) => {
      await mutations.reviewDeliverable(id, decision, note);
    },
    markPublished: async (id: string, url: string, proofUrl?: string) => {
      await mutations.markPublished(id, url, proofUrl);
    },
    verify: async (id: string) => {
      await mutations.verifyDeliverable(id);
    },
  };

  const agreementPanel = (
    <div className="space-y-5 rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
      {collaboration.money.agreedRate !== null && (
        <div>
          <p className="adv-label mb-2">{t("marketplace.collaboration.agreedRate")}</p>
          <CommissionSplit
            rate={collaboration.money.agreedRate}
            currency={collaboration.money.currency}
            commissionPct={collaboration.money.commissionPct}
            viewerParty={collaboration.viewerParty}
          />
        </div>
      )}

      <div
        className={cn(
          collaboration.money.agreedRate !== null && "border-t border-neutral-100 pt-4",
        )}
      >
        <OfferChain
          collaboration={collaboration}
          busy={mutations.busy}
          onOffer={async (amount, message) => {
            await mutations.makeOffer(collaboration.id, amount, message);
          }}
          onAccept={async (offerId) => {
            await mutations.acceptOffer(offerId);
          }}
          onReject={async (offerId) => {
            await mutations.rejectOffer(offerId);
          }}
        />
      </div>
    </div>
  );

  const chatPanel = (
    <div className="flex h-[70vh] flex-col rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest lg:h-[520px]">
      <CollaborationChat
        messages={collaboration.messages}
        busy={mutations.busy}
        onSend={async (content) => {
          await mutations.sendMessage(collaboration.id, content);
        }}
        onTranslate={(messageId, language) => mutations.translateMessage(messageId, language)}
        onRead={() => mutations.markRead(collaboration.id)}
      />
    </div>
  );

  const deliverablesPanel = (
    <div className="space-y-3">
      {collaboration.deliverables.length === 0 ? (
        <div className="rounded-card border border-neutral-200 bg-white px-5 py-10 text-center shadow-card-rest">
          <p className="text-meta text-neutral-500">
            {collaboration.status === "NEGOTIATING"
              ? t("marketplace.collaboration.deliverable.notYet")
              : t("marketplace.collaboration.deliverable.none")}
          </p>
        </div>
      ) : (
        collaboration.deliverables.map((deliverable) => (
          <DeliverableCard
            key={deliverable.id}
            deliverable={deliverable}
            collaborationId={collaboration.id}
            viewerParty={collaboration.viewerParty}
            maxRevisions={collaboration.campaign?.maxRevisions}
            counterpartyName={counterparty.name}
            actions={deliverableActions}
            busy={mutations.busy}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="adv-page">
      <Link
        to="/colaboraciones"
        className="mb-5 inline-flex items-center gap-1.5 text-meta text-neutral-500 transition-colors hover:text-neutral-900"
      >
        <ArrowLeft className="size-4" />
        {t("marketplace.collaboration.backToInbox")}
      </Link>

      <div className="rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            {counterparty.logoUrl ? (
              <img
                src={counterparty.logoUrl}
                alt=""
                className="size-11 shrink-0 rounded-avatar-square object-cover"
              />
            ) : (
              <span className="flex size-11 shrink-0 items-center justify-center rounded-avatar-square bg-gradient-to-br from-steel-600 to-steel-400 font-semibold text-white">
                {counterparty.name.charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <h1 className="truncate text-card-title text-neutral-900">
                {counterparty.handle ? `@${counterparty.handle}` : counterparty.name}
              </h1>
              <p className="truncate text-meta text-neutral-500">
                {collaboration.campaign?.title ?? "—"}
              </p>
            </div>
          </div>

          <TurnBadge
            turnOwner={collaboration.turnOwner}
            viewerParty={collaboration.viewerParty}
            size="md"
          />
        </div>

        {/* The spine. Five milestones, told from whichever side is looking. */}
        <div className="mt-6 overflow-x-auto border-t border-neutral-100 pt-5">
          <EscrowLedger
            voice={isBrand ? "brand" : "creator"}
            current={escrowIndexForDeliverables(collaboration.deliverables)}
            amount={railAmount}
            className="min-w-[540px]"
          />
        </div>
      </div>

      {/* Mobile: tabs. Desktop: content plus a sticky aside. */}
      <div className="mt-4 flex items-center gap-2 lg:hidden">
        {(
          [
            ["deliverables", Package],
            ["chat", MessageCircle],
            ["agreement", Handshake],
          ] as const
        ).map(([value, Icon]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={cn(
              "inline-flex flex-1 items-center justify-center gap-1.5 rounded-control border px-3 py-2 text-fine font-medium transition-colors",
              tab === value
                ? "border-steel-100 bg-steel-50 text-steel-600"
                : "border-neutral-200 text-neutral-500",
            )}
          >
            <Icon className="size-3.5" />
            {t(`marketplace.collaboration.tab.${value}`)}
          </button>
        ))}
      </div>

      <div className="mt-4 hidden flex-wrap items-start gap-5 lg:flex">
        <div className="min-w-0 flex-[1_1_480px]">{deliverablesPanel}</div>
        <div className="min-w-0 flex-[1_1_320px] space-y-4 lg:sticky lg:top-[76px]">
          {agreementPanel}
          {chatPanel}
        </div>
      </div>

      <div className="mt-4 lg:hidden">
        {tab === "agreement" && agreementPanel}
        {tab === "chat" && chatPanel}
        {tab === "deliverables" && deliverablesPanel}
      </div>
    </div>
  );
}
